/* ==========================================================================
   VELOCITY — motion & interaction system
   Progressive enhancement: works fully without GSAP; enriches when present.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';
  var hasST = hasGSAP && typeof window.ScrollTrigger !== 'undefined';
  var hasSplit = hasGSAP && typeof window.SplitText !== 'undefined';

  if (hasST) { window.gsap.registerPlugin(window.ScrollTrigger); }
  if (hasSplit) { window.gsap.registerPlugin(window.SplitText); }

  function $(s, c) { return (c || doc).querySelector(s); }
  function $all(s, c) { return Array.prototype.slice.call((c || doc).querySelectorAll(s)); }
  function on(el, ev, fn, opt) { if (el) el.addEventListener(ev, fn, opt || false); }

  /* ---------------- Reveal on scroll (IntersectionObserver) --------------- */
  function initReveals() {
    var els = $all('[data-reveal]');
    if (!els.length || !root.classList.contains('v-animate')) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------- Split-text headline reveal (GSAP) --------------------- */
  function initSplitHeadlines() {
    var heads = $all('[data-split]');
    if (!heads.length) return;
    if (!hasSplit || reduceMotion || !root.classList.contains('v-animate')) {
      heads.forEach(function (h) { h.style.opacity = '1'; });
      return;
    }
    heads.forEach(function (h) {
      h.style.opacity = '1';
      try {
        var split = new window.SplitText(h, { type: 'lines,words', linesClass: 'v-split-line', wordsClass: 'v-split-word' });
        window.gsap.set(split.words, { yPercent: 118, opacity: 0 });
        var play = function () {
          window.gsap.to(split.words, {
            yPercent: 0, opacity: 1, duration: 0.9, ease: 'expo.out',
            stagger: 0.045, rotate: 0
          });
        };
        if (hasST) {
          window.ScrollTrigger.create({ trigger: h, start: 'top 88%', once: true, onEnter: play });
        } else { play(); }
      } catch (e) { h.style.opacity = '1'; }
    });
  }

  /* ---------------- Parallax (GSAP ScrollTrigger) ------------------------- */
  function initParallax() {
    if (!hasST || reduceMotion) return;
    $all('[data-parallax]').forEach(function (el) {
      var depth = parseFloat(el.getAttribute('data-parallax')) || 12;
      window.gsap.fromTo(el, { yPercent: -depth }, {
        yPercent: depth, ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* ---------------- Animated counters ------------------------------------ */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-count-decimals') | 0);
    if (reduceMotion) { el.textContent = target.toFixed(decimals); return; }
    var dur = 1600, start = null, from = 0;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = from + (target - from) * eased;
      el.textContent = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString();
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = decimals ? target.toFixed(decimals) : Math.round(target).toLocaleString();
    }
    requestAnimationFrame(step);
  }
  function initCounters() {
    var els = $all('[data-count]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(animateCount); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { animateCount(entry.target); io.unobserve(entry.target); }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }
  function initBars() {
    var bars = $all('[data-bar]');
    if (!bars.length || !('IntersectionObserver' in window)) {
      bars.forEach(function (b) { b.style.width = (b.getAttribute('data-bar') || 100) + '%'; });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.width = (entry.target.getAttribute('data-bar') || 100) + '%';
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    bars.forEach(function (b) { io.observe(b); });
  }

  /* ---------------- Header behaviour ------------------------------------- */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var lastY = window.scrollY, ticking = false;
    function update() {
      var y = window.scrollY;
      if (y > 40) header.classList.add('is-solid'); else header.classList.remove('is-solid');
      if (y > 260 && y > lastY) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      lastY = y; ticking = false;
    }
    on(window, 'scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------------- Mobile navigation ------------------------------------ */
  function initMobileNav() {
    var nav = $('[data-mobile-nav]');
    var openBtn = $('[data-mobile-open]');
    if (!nav) return;
    var closeEls = $all('[data-mobile-close]', nav).concat($all('[data-mobile-close]'));
    function open() { nav.setAttribute('aria-hidden', 'false'); doc.body.style.overflow = 'hidden'; }
    function close() { nav.setAttribute('aria-hidden', 'true'); doc.body.style.overflow = ''; }
    on(openBtn, 'click', open);
    closeEls.forEach(function (el) { on(el, 'click', close); });
    on(doc, 'keydown', function (e) { if (e.key === 'Escape') close(); });
    // expandable submenus
    $all('[data-submenu-toggle]', nav).forEach(function (btn) {
      on(btn, 'click', function () {
        var sub = btn.nextElementSibling;
        if (sub) sub.hidden = !sub.hidden;
        btn.classList.toggle('is-open');
      });
    });
  }

  /* ---------------- Custom cursor ---------------------------------------- */
  function initCursor() {
    if (!root.classList.contains('v-cursor-on')) return;
    var cursor = $('[data-cursor]');
    if (!cursor) return;
    var ring = $('.v-cursor__ring', cursor);
    var dot = $('.v-cursor__dot', cursor);
    var label = $('[data-cursor-label]', cursor);
    var mx = window.innerWidth / 2, my = window.innerHeight / 2;
    var rx = mx, ry = my;
    on(window, 'mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      if (dot) dot.style.transform = 'translate(' + mx + 'px,' + my + 'px) translate(-50%,-50%)';
      if (label) label.style.transform = 'translate(' + mx + 'px,' + my + 'px) translate(-50%,-50%)';
    });
    function loop() {
      rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
      if (ring) ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    }
    // reposition dot uses left/top 0 base — set base
    if (dot) dot.style.left = dot.style.top = '0';
    if (ring) ring.style.left = ring.style.top = '0';
    if (label) label.style.left = label.style.top = '0';
    requestAnimationFrame(loop);

    var interactive = 'a, button, [data-cursor-hover], input, select, textarea, summary';
    on(doc, 'mouseover', function (e) {
      var t = e.target.closest(interactive);
      if (t) {
        cursor.classList.add('is-active');
        var lb = t.getAttribute('data-cursor-label');
        if (label) label.textContent = lb || '';
      }
    });
    on(doc, 'mouseout', function (e) {
      var t = e.target.closest(interactive);
      if (t && !(e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(interactive))) {
        cursor.classList.remove('is-active');
        if (label) label.textContent = '';
      }
    });
  }

  /* ---------------- Magnetic buttons ------------------------------------- */
  function initMagnetic() {
    if (reduceMotion || !window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    $all('[data-magnetic]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.3;
      on(el, 'mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        if (hasGSAP) window.gsap.to(el, { x: x, y: y, duration: 0.4, ease: 'power3.out' });
        else el.style.transform = 'translate(' + x + 'px,' + y + 'px)';
      });
      on(el, 'mouseleave', function () {
        if (hasGSAP) window.gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.4)' });
        else el.style.transform = '';
      });
    });
  }

  /* ---------------- Accordion / FAQ -------------------------------------- */
  function initAccordions() {
    $all('[data-accordion]').forEach(function (acc) {
      var single = acc.hasAttribute('data-accordion-single');
      $all('[data-accordion-trigger]', acc).forEach(function (trigger) {
        on(trigger, 'click', function () {
          var item = trigger.closest('.v-accordion__item');
          var panel = $('.v-accordion__panel', item);
          var isOpen = item.classList.contains('is-open');
          if (single) {
            $all('.v-accordion__item.is-open', acc).forEach(function (o) {
              o.classList.remove('is-open');
              var p = $('.v-accordion__panel', o); if (p) p.style.maxHeight = null;
              var t = $('[data-accordion-trigger]', o); if (t) t.setAttribute('aria-expanded', 'false');
            });
          }
          if (isOpen) {
            item.classList.remove('is-open'); panel.style.maxHeight = null;
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open'); panel.style.maxHeight = panel.scrollHeight + 'px';
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---------------- Back to top ------------------------------------------ */
  function initToTop() {
    var btn = $('[data-to-top]');
    if (!btn) return;
    on(btn, 'click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    var ticking = false;
    on(window, 'scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          if (window.scrollY > window.innerHeight) btn.classList.add('is-visible');
          else btn.classList.remove('is-visible');
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------------- Table of contents scrollspy -------------------------- */
  function initTOC() {
    var toc = $('[data-toc]');
    if (!toc) return;
    var links = $all('a', toc);
    var map = {};
    links.forEach(function (l) {
      var id = l.getAttribute('href');
      if (id && id.charAt(0) === '#') { var t = doc.getElementById(id.slice(1)); if (t) map[id] = t; }
    });
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          links.forEach(function (l) {
            l.classList.toggle('is-active', l.getAttribute('href') === '#' + entry.target.id);
          });
        }
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    Object.keys(map).forEach(function (k) { io.observe(map[k]); });
  }

  /* ---------------- Footer year ------------------------------------------ */
  function initYear() {
    $all('[data-year]').forEach(function (el) { el.textContent = new Date().getFullYear(); });
  }

  /* ---------------- Page load sequence ----------------------------------- */
  function initLoadSequence() {
    doc.body.classList.add('is-loaded');
  }

  /* ---------------- Boot -------------------------------------------------- */
  function boot() {
    initHeader();
    initMobileNav();
    initCursor();
    initMagnetic();
    initReveals();
    initSplitHeadlines();
    initParallax();
    initCounters();
    initBars();
    initAccordions();
    initToTop();
    initTOC();
    initYear();
    initLoadSequence();
    if (hasST) { setTimeout(function () { window.ScrollTrigger.refresh(); }, 300); }
  }

  if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', boot);
  else boot();

  // Re-init for Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', function () {
    initReveals(); initSplitHeadlines(); initCounters(); initBars(); initAccordions();
    if (hasST) window.ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:reorder', function () { if (hasST) window.ScrollTrigger.refresh(); });

  window.Velocity = { animateCount: animateCount };
})();
